"use client";

import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Flex, Grid, Skeleton, Tag, Tour, Typography } from "antd/lib";
import { useRouter } from "next/navigation";
import { Event, EventParticipantsResponse } from "@/types/events";
import FadeIn from "@/src/components/Common/FadeIn/FadeIn";
import { editEventUrl, ongoingMyEventsUrl, publicEventsUrl } from "@/utils/appUrls";
import dayjs from "dayjs";
import { useAuth0 } from "@auth0/auth0-react";
import XStroke from "../../../public/icons/XStroke";
import { useSidebarTutorial } from "@/context/SidebarTutorialContext";
import { useEventsFetchers } from "@/hooks/useEventsFetchers";
import { analytics } from "@/constants/common";
import {
  getEventTypeTagText,
  getFormattedManualLocation,
  isMobileAudioEvent,
  isMobileAudioMaterialEvent,
  lazyRetry,
  reportForbiddenAccessFromListing,
} from "@/utils/helpers";
import styles from "./Events.module.scss";
import { App } from "antd";
import dynamic from "next/dynamic";
import EventParticipants from "@/src/components/Events/EventParticipants";
import EventQrCode from "@/src/components/Events/EventQrCode";
import { EventsActivityTypes } from "@/constants/events";
import utc from "dayjs/plugin/utc";
import { isDayAfter, isDayBefore } from "@/utils/forms";
import NotFound from "@/src/components/NotFound/NotFound";

const RegisterEventNowModal = dynamic(
  () => lazyRetry(() => import("@/src/components/Events/modals/RegisterEventNowModal")),
  {
    ssr: false,
  },
);
const EventAttachmentsModal = dynamic(
  () => lazyRetry(() => import("@/src/components/Events/modals/EventAttachmentsModal")),
  {
    ssr: false,
  },
);

const { Text, Title } = Typography;

const { useBreakpoint } = Grid;

dayjs.extend(utc);

interface EventDetailsProps {
  event?: Event;
  eventIsLoading?: boolean;
  id?: string;
  isMyEvents?: boolean;
  isInReportingContext?: boolean;
}

const EventDetails: React.FC<EventDetailsProps> = ({
  id,
  event,
  eventIsLoading = false,
  isMyEvents = false,
  isInReportingContext = false,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth0();
  const { eventDetailsPageTourIsOpen, tourWasShown, setTourWasShown } = useSidebarTutorial();
  const { deleteEvent, fetchEventDetails } = useEventsFetchers();
  const ref1 = useRef(null);
  const screens = useBreakpoint();
  const { message } = App.useApp();

  const [fetchedEvent, setFetchedEvent] = useState(event);
  const [loading, setLoading] = useState(true);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [deletingEventIsLoading, setDeletingEventIsLoading] = useState(false);
  const [participants, setParticipants] = useState<EventParticipantsResponse>();
  const [participantsAreLoading, setParticipantsAreLoading] = useState(true);
  const [tourIsOpen, setTourIsOpen] = useState(eventDetailsPageTourIsOpen);
  const [participantsAreRendered, setParticipantsAreRendered] = useState(false);

  const steps = [
    {
      title: t("common.tour.myEventDetails.steps.participants.title"),
      description: t("common.tour.myEventDetails.steps.participants.description"),
      target: () => ref1.current,
    },
  ];

  useEffect(() => {
    if (!event) return;

    setFetchedEvent(event);
    setLoading(false);
  }, [event]);

  useEffect(() => {
    if (tourIsOpen !== eventDetailsPageTourIsOpen) setTourIsOpen(eventDetailsPageTourIsOpen);
  }, [eventDetailsPageTourIsOpen]);

  useEffect(() => {
    if (eventIsLoading || fetchedEvent) return;

    if (!id) {
      router.push(isMyEvents ? ongoingMyEventsUrl() : publicEventsUrl());
      return;
    }

    const fetchEvent = async () => {
      const eventResponse = await fetchEventDetails(id, isMyEvents, setLoading);
      reportForbiddenAccessFromListing(!!eventResponse, id, "My event");

      if (eventResponse) {
        setFetchedEvent(eventResponse.data[0]);
      }

      setLoading(false);
    };

    fetchEvent();
  }, [id, fetchedEvent]);

  const copyLinkToClipboard = async (registrationUrl: string) => {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      message.success(t("eventDetailsPage.messages.linkCopied"));
    } catch (err) {
      message.error(t("common.messages.linkCopyFail"));
      setLoading(false);
    }
  };

  const deleteEventHandler = async () => {
    if (!fetchedEvent?.uuid) return;

    setDeletingEventIsLoading(true);

    await deleteEvent(fetchedEvent.uuid, setDeletingEventIsLoading);
    analytics.track("Event deleted", { value: JSON.stringify({ eventId: fetchedEvent.uuid }) });

    router.push(ongoingMyEventsUrl());

    setDeletingEventIsLoading(false);
    message.success(t("eventCrudPage.messages.eventDeleted"));
  };

  const userTeamdeskId = user?.app_teamdeskId;
  const isEventCreator = !!userTeamdeskId && userTeamdeskId === fetchedEvent?.creator_id;
  const isMainFacilitator = !!userTeamdeskId && userTeamdeskId === fetchedEvent?.main_facilitator_id;
  const isAssistantFacilitator =
    !!userTeamdeskId &&
    !!fetchedEvent?.assistant_facilitators_ids
      ?.split(",")
      .map((id) => id.trim())
      .includes(userTeamdeskId);

  return (
    <Flex vertical gap={16}>
      <Flex
        justify="space-between"
        align={screens.sm ? "center" : "flex-start"}
        gap={16}
        className="w-full"
        vertical={!screens.sm}
      >
        <Title level={5} style={{ marginBottom: 0, flexShrink: 0 }}>
          {isInReportingContext ? t("reportDetailsPage.sections.eventInfo.title") : t("eventDetailsPage.title")}
        </Title>
        <Flex
          gap={16}
          align={screens.md ? "center" : "flex-start"}
          vertical={!screens.sm}
          className={screens.sm ? "" : "w-full"}
        >
          {isMyEvents && !loading && !isInReportingContext && (
            <>
              {!!fetchedEvent && isDayAfter(dayjs.utc(fetchedEvent?.start_date), dayjs()) && (
                <Button
                  style={{ minWidth: 150 }}
                  className={screens.sm ? "" : "w-full"}
                  type="primary"
                  onClick={() => router.push(editEventUrl(fetchedEvent.uuid))}
                >
                  {t("eventDetailsPage.buttons.edit")}
                </Button>
              )}
              {!!fetchedEvent?.event_link &&
                !isDayBefore(dayjs.utc(fetchedEvent?.registration_deadline_date), dayjs()) && (
                  <Button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    className={screens.sm ? "" : "w-full"}
                    onClick={() => copyLinkToClipboard(fetchedEvent.event_link)}
                  >
                    <i className={`icon-link_stroke`}></i>
                    {t(
                      isMobileAudioEvent(fetchedEvent)
                        ? "eventDetailsPage.eventLink"
                        : "eventDetailsPage.registrationLink",
                    )}
                  </Button>
                )}
              {!!fetchedEvent && !fetchedEvent?.reported && (
                <Button
                  onClick={deleteEventHandler}
                  loading={deletingEventIsLoading}
                  className={screens.sm ? "" : "w-full"}
                >
                  <span className="btn-text">{t("eventDetailsPage.buttons.delete")}</span>
                </Button>
              )}
            </>
          )}
          {!isMyEvents && !loading && !!fetchedEvent && !isMobileAudioEvent(fetchedEvent) && (
            <Button
              style={{ minWidth: 150 }}
              className={screens.sm ? "" : "w-full"}
              type="primary"
              onClick={() => setIsRegisterModalOpen(true)}
              disabled={isDayBefore(dayjs.utc(fetchedEvent?.registration_deadline_date), dayjs())}
            >
              {t("eventDetailsPage.buttons.register")}
            </Button>
          )}
          <Button onClick={() => router.back()} type="text" className={screens.sm ? "" : "w-full"}>
            {t("common.actions.back")}
          </Button>
        </Flex>
      </Flex>
      {fetchedEvent || loading ? (
        <Skeleton active loading={loading}>
          <FadeIn>
            <Flex vertical gap={16}>
              {isMyEvents &&
                (isEventCreator ? (
                  <Text>{t("eventDetailsPage.creator")}</Text>
                ) : isMainFacilitator ? (
                  <Text>{t("eventDetailsPage.mainFacilitatorRole")}</Text>
                ) : isAssistantFacilitator ? (
                  <Text>{t("eventDetailsPage.assistant")}</Text>
                ) : null)}
              <Flex justify="space-between" gap={screens.xl ? 64 : 24} vertical={!screens.md}>
                <Flex vertical gap={16} flex={1} style={{ overflow: "auto" }}>
                  <Flex>
                    <Tag variant="filled" color="cyan">
                      {fetchedEvent && getEventTypeTagText(t, fetchedEvent.event_type, fetchedEvent.equipping_level)}
                    </Tag>
                    <Tag variant="filled" color="green">
                      {fetchedEvent?.online ? t("eventsPage.tags.online") : t("eventsPage.tags.inPerson")}
                    </Tag>
                    {isMyEvents && (
                      <Tag variant="filled" color="green">
                        {fetchedEvent?.public ? t("eventsPage.tags.public") : t("eventsPage.tags.private")}
                      </Tag>
                    )}
                  </Flex>
                  <Flex vertical gap={16}>
                    <Title level={4} style={{ marginBottom: 0 }}>
                      {fetchedEvent?.title}
                    </Title>
                    <Flex vertical gap={40}>
                      <Flex vertical gap={18}>
                        <Flex vertical justify="space-between" style={{ width: "100%", gap: 8, textAlign: "left" }}>
                          <Flex justify="space-between" style={{ width: "100%", textAlign: "start" }} gap={16}>
                            <Text className={styles.eventDetailsCardText}>{t("eventDetailsPage.mainFacilitator")}</Text>
                            <Text strong className={styles.eventDetailsCardText}>
                              {fetchedEvent?.main_facilitator}
                            </Text>
                          </Flex>
                          {!!fetchedEvent?.assistant_facilitators && (
                            <Flex justify="space-between" style={{ width: "100%", textAlign: "start" }} gap={16}>
                              <Text className={styles.eventDetailsCardText}>
                                {t("eventDetailsPage.assistantFacilitators")}
                              </Text>
                              <Text strong className={styles.eventDetailsCardText}>
                                {fetchedEvent.assistant_facilitators}
                              </Text>
                            </Flex>
                          )}
                          <Flex justify="space-between" className="w-full" gap={16}>
                            <Text className={styles.eventDetailsCardText}>{t("eventDetailsPage.contact")}</Text>
                            <Text strong className={styles.eventDetailsCardText}>
                              {fetchedEvent?.contact_email}
                            </Text>
                          </Flex>
                          <Flex justify="space-between" className="w-full" gap={16}>
                            <Text className={styles.eventDetailsCardText}>{t("eventDetailsPage.host")}</Text>
                            <Text strong className={styles.eventDetailsCardText}>
                              {fetchedEvent?.host_organizations}
                            </Text>
                          </Flex>
                          <Flex justify="space-between" className="w-full" gap={16}>
                            <Text className={styles.eventDetailsCardText}>
                              {t("eventDetailsPage.registrationDeadline")}
                            </Text>
                            <Text strong className={styles.eventDetailsCardText}>
                              {dayjs.utc(fetchedEvent?.registration_deadline_date).format("D MMMM YYYY")}
                            </Text>
                          </Flex>
                          {!!fetchedEvent?.prerequisites && (
                            <Flex justify="space-between" className="w-full" gap={16}>
                              <Text className={styles.eventDetailsCardText}>{t("eventDetailsPage.requirements")}</Text>
                              <Text strong className={styles.eventDetailsCardText}>
                                {fetchedEvent.prerequisites}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                        {!!fetchedEvent?.details && <p style={{ marginTop: 8 }}>{fetchedEvent.details}</p>}
                      </Flex>
                      {isMyEvents && !!fetchedEvent?.uuid && !!fetchedEvent.code && !fetchedEvent.reported && (
                        <EventQrCode
                          value={fetchedEvent.code}
                          eventId={fetchedEvent.id}
                          eventTitle={fetchedEvent.title}
                          size={160}
                        />
                      )}
                      {isMyEvents &&
                        !!fetchedEvent?.uuid &&
                        !(
                          isMobileAudioMaterialEvent(fetchedEvent) &&
                          fetchedEvent.event_type === EventsActivityTypes.healing
                        ) && (
                          <EventParticipants
                            eventId={fetchedEvent.uuid}
                            eventType={fetchedEvent.event_type}
                            equippingLevel={fetchedEvent.equipping_level}
                            mainFacilitatorId={fetchedEvent.main_facilitator_id}
                            materialsRequired={
                              fetchedEvent.event_type !== EventsActivityTypes.cop && !fetchedEvent.material_id
                            }
                            ageRequired={!fetchedEvent.age_id}
                            participants={participants}
                            participantsAreLoading={participantsAreLoading}
                            setParticipants={setParticipants}
                            setParticipantsAreLoading={setParticipantsAreLoading}
                            withActions={!fetchedEvent.reported && !isMobileAudioEvent(fetchedEvent)}
                            participantsRef={ref1}
                            setParticipantsAreRendered={setParticipantsAreRendered}
                          />
                        )}
                    </Flex>
                  </Flex>
                </Flex>
                <Flex vertical gap={16} style={{ width: screens.xl ? 350 : screens.md ? 280 : "100%", flexShrink: 0 }}>
                  <Flex gap={8}>
                    {!!fetchedEvent?.registration_fee && (
                      <Card size="small" style={{ flex: 1 }}>
                        <Flex vertical justify="space-between" gap={8} style={{ minWidth: 115 }}>
                          <Text>{t("eventDetailsPage.registrationFee")}</Text>
                          <Text strong>{fetchedEvent.registration_fee}</Text>
                        </Flex>
                      </Card>
                    )}
                    <Card size="small" style={{ flex: 1 }}>
                      <Flex vertical justify="space-between" gap={8}>
                        <Flex gap={4} align="center">
                          <i className={`icon-world_stroke`} />
                          <Text>{t("eventDetailsPage.language")}</Text>
                        </Flex>
                        <Text strong>{fetchedEvent?.main_language}</Text>
                      </Flex>
                    </Card>
                  </Flex>
                  <Card size="small">
                    <Flex vertical gap={8}>
                      <Flex align="center" gap={4}>
                        <i className={`icon-calendar_stroke`} />
                        <Text>{t("eventDetailsPage.startDate")}</Text>
                      </Flex>
                      <Text strong>{dayjs.utc(fetchedEvent?.start_date).format("D MMMM YYYY")}</Text>
                    </Flex>
                  </Card>
                  <Card size="small">
                    <Flex vertical gap={8}>
                      <Flex align="center" gap={4}>
                        <i className={`icon-calendar_stroke`} />
                        <Text>{t("eventDetailsPage.endDate")}</Text>
                      </Flex>
                      <Text strong>{dayjs.utc(fetchedEvent?.end_date).format("D MMMM YYYY")}</Text>
                    </Flex>
                  </Card>
                  <Card size="small">
                    <Flex vertical gap={8}>
                      <Flex align="center" gap={4}>
                        <i className={`icon-clock_stroke`} />
                        <Text>{t("eventDetailsPage.schedulingTips")}</Text>
                      </Flex>
                      <Text strong>{fetchedEvent?.schedule_tips}</Text>
                    </Flex>
                  </Card>
                  <Card size="small">
                    <Flex vertical gap={8}>
                      <Flex align="center" gap={4}>
                        <i className={`icon-clock_stroke`} />
                        <Text>{t("eventDetailsPage.timeZone")}</Text>
                      </Flex>
                      <Text
                        strong
                        style={{
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          maxWidth: 229,
                        }}
                      >
                        {fetchedEvent?.timezone}
                      </Text>
                    </Flex>
                  </Card>
                  {(!!fetchedEvent?.location_place || fetchedEvent?.manual_location) && (
                    <Card size="small">
                      <Flex vertical gap={8}>
                        <Flex align="center" gap={4}>
                          <i className={`icon-flag_stroke`} />
                          <Text>{t("eventDetailsPage.location")}</Text>
                        </Flex>
                        <Text strong>
                          {fetchedEvent?.manual_location
                            ? getFormattedManualLocation(fetchedEvent)
                            : fetchedEvent.location_place}
                        </Text>
                      </Flex>
                    </Card>
                  )}
                  {!!fetchedEvent?.host_country && (
                    <Card size="small">
                      <Flex vertical gap={8}>
                        <Flex align="center" gap={4}>
                          <i className={`icon-flag_stroke`} />
                          <Text>{t("eventDetailsPage.hostCountry")}</Text>
                        </Flex>
                        <Text strong>{fetchedEvent.host_country}</Text>
                      </Flex>
                    </Card>
                  )}
                  {!!fetchedEvent?.attachment_link && (
                    <Button
                      style={{ width: 155 }}
                      icon={<i className={`icon-paperclip_stroke`} />}
                      onClick={() => setIsAttachmentModalOpen(true)}
                    >
                      {t("eventDetailsPage.buttons.attachments")}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </FadeIn>
        </Skeleton>
      ) : (
        <NotFound />
      )}

      {!!fetchedEvent && (
        <RegisterEventNowModal
          registrationLink={fetchedEvent.registration_link}
          id={fetchedEvent.uuid}
          isModalOpen={isRegisterModalOpen}
          setIsModalOpen={setIsRegisterModalOpen}
        />
      )}

      {!!fetchedEvent?.attachment_link && !!fetchedEvent?.attachment_filename && (
        <EventAttachmentsModal
          attachmentFileName={fetchedEvent.attachment_filename}
          attachmentLink={fetchedEvent.attachment_link}
          isModalOpen={isAttachmentModalOpen}
          setIsModalOpen={setIsAttachmentModalOpen}
        />
      )}

      {isMyEvents && participantsAreRendered && (
        <Tour
          placement="right"
          open={tourIsOpen}
          closeIcon={
            <XStroke
              color="#000"
              onClick={() => {
                setTourIsOpen(false);
                setTourWasShown({ ...tourWasShown, eventDetailsPage: true });
              }}
            />
          }
          onFinish={() => {
            setTourIsOpen(false);
            setTourWasShown({ ...tourWasShown, eventDetailsPage: true });
          }}
          steps={steps}
          indicatorsRender={() => <></>}
        />
      )}
    </Flex>
  );
};

export default EventDetails;
